import {
	AlertTriangle,
	ArrowRight,
	Calendar,
	CheckCircle,
	Clock,
	Target,
	Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface AssignedTopic {
	topic_id: string;
	title: string;
	description: string;
	initial_question?: string;
	assignment_id: string;
	due_date: string | null;
	priority: number;
	assigned_at: string;
	group_id: string;
	group_name: string;
	completed_at: string | null;
	completed_reflection_id: string | null;
	status: "completed" | "overdue" | "pending";
}

interface AssignedTopicsProps {
	onTopicSelect?: (topic: AssignedTopic) => void;
	showStartButton?: boolean;
}

export function AssignedTopics({
	onTopicSelect,
	showStartButton = true,
}: AssignedTopicsProps) {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [assignedTopics, setAssignedTopics] = useState<AssignedTopic[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchAssignedTopics();
	}, []);

	const fetchAssignedTopics = async () => {
		// 🔒 SECURITY: Verify user is authenticated before fetching data
		if (!user?.id) {
			console.error("No authenticated user - cannot fetch assigned topics");
			setAssignedTopics([]);
			setLoading(false);
			return;
		}

		// 🔒 SECURITY: Get user's groups first, then fetch assignments for those groups
		// First, get the groups the user belongs to
		const { data: userGroups, error: groupError } = await supabase
			.from("group_memberships")
			.select("group_id")
			.eq("mentee_id", user.id);

		if (groupError) {
			console.error("Error fetching user groups - Full details:", {
				message: groupError.message,
				details: groupError.details,
				hint: groupError.hint,
				code: groupError.code,
				error: groupError,
			});
			setAssignedTopics([]);
			setLoading(false);
			return;
		}

		if (!userGroups || userGroups.length === 0) {
			// User is not a member of any groups - this is normal for new users
			setAssignedTopics([]);
			setLoading(false);
			return;
		}

		const groupIds = userGroups.map((g) => g.group_id);

		// Now fetch topic assignments for those groups
		const { data, error } = await supabase
			.from("topic_assignments")
			.select(
				`
        *,
        topics(name, description),
        groups(name)
      `,
			)
			.in("group_id", groupIds) // CRITICAL: Only get assignments for user's groups
			.order("priority", { ascending: false })
			.order("created_at", { ascending: false });

		if (error) {
			console.error("Error fetching assigned topics - Full error details:", {
				message: error.message,
				details: error.details,
				hint: error.hint,
				code: error.code,
				error: error,
			});
			console.error("Query attempted:", {
				step: "topic_assignments",
				table: "topic_assignments",
				userId: user.id,
				groupIds: groupIds,
				filter: "group_id IN groupIds",
			});
		} else {
			// Successfully fetched assigned topics

			// Transform the joined data to match our interface
			const transformedTopics = (data || []).map((item: any) => ({
				topic_id: item.topic_id,
				title: item.topics?.name || "Unknown Topic",
				description: item.topics?.description || "",
				assignment_id: item.id,
				due_date: item.due_date,
				priority: item.priority || 1,
				assigned_at: item.created_at,
				group_id: item.group_id,
				group_name: item.groups?.name || "Unknown Group",
				completed_at: null, // This would need to be determined from topic_completions
				completed_reflection_id: null,
				status: "pending" as const, // Default status, could be enhanced with completion logic
			}));

			setAssignedTopics(transformedTopics);
		}
		setLoading(false);
	};

	const handleTopicSelect = (topic: AssignedTopic) => {
		if (onTopicSelect) {
			onTopicSelect(topic);
		} else {
			// Navigate to reflection flow with topic
			navigate(
				`/reflection/new?topic=${topic.topic_id}&assignment=${topic.assignment_id}`,
			);
		}
	};

	const getPriorityColor = (priority: number) => {
		switch (priority) {
			case 3:
				return "bg-red-100 text-red-800";
			case 2:
				return "bg-yellow-100 text-yellow-800";
			default:
				return "bg-blue-100 text-blue-800";
		}
	};

	const getPriorityText = (priority: number) => {
		switch (priority) {
			case 3:
				return "High";
			case 2:
				return "Medium";
			default:
				return "Low";
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "overdue":
				return "bg-red-100 text-red-800";
			default:
				return "bg-blue-100 text-blue-800";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="w-4 h-4" />;
			case "overdue":
				return <AlertTriangle className="w-4 h-4" />;
			default:
				return <Clock className="w-4 h-4" />;
		}
	};

	const formatDueDate = (dueDate: string | null) => {
		if (!dueDate) return null;

		const date = new Date(dueDate);
		const now = new Date();
		const diffDays = Math.ceil(
			(date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
		);

		if (diffDays < 0) {
			return `${Math.abs(diffDays)} days overdue`;
		} else if (diffDays === 0) {
			return "Due today";
		} else if (diffDays === 1) {
			return "Due tomorrow";
		} else {
			return `Due in ${diffDays} days`;
		}
	};

	if (loading) {
		return (
			<div className="text-center py-8">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
				<p className="text-muted-foreground">Loading assigned topics...</p>
			</div>
		);
	}

	if (assignedTopics.length === 0) {
		return null;
	}

	const pendingTopics = assignedTopics.filter((t) => t.status !== "completed");
	const completedTopics = assignedTopics.filter(
		(t) => t.status === "completed",
	);

	return (
		<div className="space-y-6">
			{/* Pending Topics */}
			{pendingTopics.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="text-lg flex items-center space-x-2">
							<Target className="w-5 h-5" />
							<span>Assigned Reflection Topics</span>
						</CardTitle>
						<CardDescription>
							Complete these reflection topics assigned by your mentor
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{pendingTopics.map((topic) => (
								<div
									key={topic.assignment_id}
									className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
								>
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<h4 className="font-medium">{topic.title}</h4>
											<Badge
												className={getStatusColor(topic.status)}
												variant="outline"
											>
												<div className="flex items-center space-x-1">
													{getStatusIcon(topic.status)}
													<span className="capitalize">{topic.status}</span>
												</div>
											</Badge>
										</div>

										{topic.description && (
											<p className="text-sm text-muted-foreground">
												{topic.description}
											</p>
										)}

										<div className="flex items-center space-x-4 text-xs text-muted-foreground">
											<span className="flex items-center space-x-1">
												<Users className="w-3 h-3" />
												<span>From {topic.group_name}</span>
											</span>
											{topic.due_date && (
												<span className="flex items-center space-x-1">
													<Calendar className="w-3 h-3" />
													<span>{formatDueDate(topic.due_date)}</span>
												</span>
											)}
										</div>

										{showStartButton && (
											<div className="pt-2 border-t">
												<Button
													data-testid="start-assigned-topic-button"
													onClick={() => handleTopicSelect(topic)}
													disabled={topic.status === "completed"}
													className="w-full"
												>
													{topic.status === "completed"
														? "Completed"
														: "Start Reflection"}
													<ArrowRight className="w-4 h-4 ml-2" />
												</Button>
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Completed Topics */}
			{completedTopics.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center space-x-2">
							<CheckCircle className="w-5 h-5 text-green-600" />
							<span>Completed Topics</span>
						</CardTitle>
						<CardDescription>
							Reflection topics you have already completed
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-3">
							{completedTopics.map((topic) => (
								<div
									key={topic.assignment_id}
									className="border rounded-lg p-3 bg-green-50 opacity-75"
								>
									<div className="flex items-center justify-between">
										<div className="flex-1">
											<div className="flex items-center space-x-2 mb-1">
												<h5 className="font-medium text-sm">{topic.title}</h5>
												<Badge className="bg-green-100 text-green-800">
													Completed
												</Badge>
											</div>
											<div className="flex items-center space-x-4 text-xs text-muted-foreground">
												<span>From {topic.group_name}</span>
												<span>
													Completed{" "}
													{new Date(topic.completed_at!).toLocaleDateString()}
												</span>
											</div>
										</div>
										{topic.completed_reflection_id && (
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													navigate(
														`/reflection/${topic.completed_reflection_id}`,
													)
												}
											>
												View Reflection
											</Button>
										)}
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
